// src/routes/index.jsx

import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../contexts/UserContext';
import { useFeatureFlags } from '../contexts/FeatureContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { FeatureGuard } from '../components/layout/FeatureGuard';
import { isExerciseOnlyMode } from '../config/navigationConfig';
import { PermissionGuard } from '../components/layout/PermissionGuard';

import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { RouteErrorBoundary } from '../components/layout/RouteErrorBoundary';
import { RouteFallback, FullPageFallback } from '../components/layout/RouteFallback';
import { LoadFailureCard } from '../components/ui/LoadFailureCard';
import { authService } from '../services/auth';
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/utils/staleChunk';
import { useOfflineSessionStore } from '@/services/offline';
import OfflineReconnectWall from '../components/offline/OfflineReconnectWall';

// Route-level code splitting. Pages load on first visit; the shell
// (DashboardLayout, guards, contexts), Login and Home stay in the entry chunk.
//
// Stale-chunk handling: after a deploy the previous build's hashed assets are
// gone, so a client on the old index.html 404s on its next lazy route. Reload
// once (60s guard) and keep the Suspense spinner up until the reload lands;
// a second failure falls through to RouteErrorBoundary. Auto-reloading here is
// safe because <Routes> has already unmounted the previous page — handler-level
// `await import()` calls elsewhere must NOT do this (unsaved work).
const load = (loader, exportName) => () =>
  loader()
    .then((m) => {
      const C = exportName ? m[exportName] : m.default;
      if (!C) throw new Error(`lazy route: '${exportName ?? 'default'}' is not exported by the module`);
      return { default: C };
    })
    .catch(async (err) => {
      if (isChunkLoadError(err) && (await reloadOnceForStaleChunk())) return new Promise(() => {});
      throw err;
    });
const lazyPage = (loader) => lazy(load(loader));
const lazyNamed = (loader, exportName) => lazy(load(loader, exportName));
const WorkoutPlans = lazyPage(() => import('../pages/WorkoutPlans'));
const WorkoutPlanDetail = lazyPage(() => import('../pages/WorkoutPlanDetail'));
import { LoginPage } from '../pages/Login';
import { HomePage } from '../pages/Home';
const PatientsPage = lazyNamed(() => import('../pages/Patients'), 'PatientsPage');
const PatientDetailsPage = lazyNamed(() => import('../pages/PatientDetails'), 'PatientDetailsPage');
const PatientEditPage = lazyNamed(() => import('../pages/PatientEdit'), 'PatientEditPage');
const MedicationsPage = lazyNamed(() => import('../pages/Medications'), 'MedicationsPage');
const PrescriptionCreatePage = lazyNamed(() => import('../pages/PrescriptionCreate'), 'PrescriptionCreatePage');
const PrescriptionReviewPage = lazyNamed(() => import('../pages/PrescriptionReview'), 'PrescriptionReviewPage');
const ProfilePage = lazyNamed(() => import('../pages/Profile'), 'ProfilePage');
const UsersPage = lazyNamed(() => import('../pages/Users'), 'UsersPage');
const UserCreatePage = lazyNamed(() => import('../pages/UserCreate'), 'UserCreatePage');
const UserEditPage = lazyNamed(() => import('../pages/UserEdit'), 'UserEditPage');
const UserDetailPage = lazyNamed(() => import('../pages/UserDetail'), 'UserDetailPage');
const CliniciansPage = lazyNamed(() => import('../pages/Clinicians'), 'CliniciansPage');
const PrescriptionHistoryPage = lazyNamed(() => import('../pages/PrescriptionHistory'), 'PrescriptionHistoryPage');
const PrescriptionDetailPage = lazyNamed(() => import('../pages/PrescriptionDetail'), 'PrescriptionDetailPage');
const RenewalRequestsPage = lazyNamed(() => import('../pages/RenewalRequests'), 'RenewalRequestsPage');
const RenewalRequestDetailPage = lazyNamed(() => import('../pages/RenewalRequestDetail'), 'RenewalRequestDetailPage');
const ChangeRequestsPage = lazyNamed(() => import('../pages/ChangeRequests'), 'ChangeRequestsPage');
const ChangeRequestDetail = lazyNamed(() => import('../pages/ChangeRequestDetail'), 'ChangeRequestDetail');
const AuditLogDashboard = lazyNamed(() => import('../pages/AuditLogDashboard'), 'AuditLogDashboard');
const MonthlyControlledSubstanceReport = lazyNamed(() => import('../pages/MonthlyControlledSubstanceReport'), 'MonthlyControlledSubstanceReport');
const OnboardingFlow = lazyPage(() => import('../pages/OnboardingFlow'));
const VeradigmCallbackHandler = lazyNamed(() => import('../components/veradigm/VeradigmCallbackHandler'), 'VeradigmCallbackHandler');
const GoogleCalendarCallbackHandler = lazyNamed(() => import('../components/google-calendar/GoogleCalendarCallbackHandler'), 'GoogleCalendarCallbackHandler');
const ExerciseLibraryPage = lazyNamed(() => import('../pages/ExerciseLibrary'), 'ExerciseLibraryPage');
const RehabProgramsPage = lazyNamed(() => import('../pages/RehabPrograms'), 'RehabProgramsPage');
const RehabProgramDetailPage = lazyNamed(() => import('../pages/RehabProgramDetail'), 'RehabProgramDetailPage');
const RehabProgramEditPage = lazyNamed(() => import('../pages/RehabProgramEdit'), 'RehabProgramEditPage');
const RoutineDetailPage = lazyNamed(() => import('../pages/RoutineDetail'), 'RoutineDetailPage');
const CPGLibraryPage = lazyNamed(() => import('../pages/CPGLibrary'), 'CPGLibraryPage');
const Forms = lazyPage(() => import('../pages/Forms'));
const FormSendHistory = lazyPage(() => import('../pages/FormSendHistory'));
const Responses = lazyPage(() => import('../pages/Responses'));
const ResponseDetails = lazyPage(() => import('../pages/ResponseDetails'));
const ResponseEditor = lazyPage(() => import('../components/forms/ResponseEditor'));
const Reports = lazyPage(() => import('../pages/Reports'));
const ReportDetails = lazyPage(() => import('../pages/ReportDetails'));
const FormRenderer = lazyPage(() => import('../components/forms/FormRenderer'));
const AIFormAssistant = lazyPage(() => import('../components/forms/AIFormAssistant'));
const CompleteFormPage = lazyPage(() => import('../pages/CompleteFormPage'));
const EncounterList = lazyNamed(() => import('../pages/encounters/EncounterList'), 'EncounterList');
const EncounterCreate = lazyNamed(() => import('../pages/encounters/EncounterCreate'), 'EncounterCreate');
const EncounterDetail = lazyNamed(() => import('../pages/encounters/EncounterDetail'), 'EncounterDetail');
const EncounterEdit = lazyNamed(() => import('../pages/encounters/EncounterEdit'), 'EncounterEdit');
const VisitSummary = lazyNamed(() => import('../pages/visits/VisitSummary'), 'VisitSummary');
const ClinicalNoteList = lazyNamed(() => import('../pages/clinical-notes/ClinicalNoteList'), 'ClinicalNoteList');
const ClinicalNoteCreate = lazyNamed(() => import('../pages/clinical-notes/ClinicalNoteCreate'), 'ClinicalNoteCreate');
const ClinicalNoteDetail = lazyNamed(() => import('../pages/clinical-notes/ClinicalNoteDetail'), 'ClinicalNoteDetail');
const ClinicalNoteEdit = lazyNamed(() => import('../pages/clinical-notes/ClinicalNoteEdit'), 'ClinicalNoteEdit');
const AppointmentList = lazyPage(() => import('../pages/appointments/AppointmentList'));
const AppointmentCalendar = lazyPage(() => import('../pages/appointments/AppointmentCalendar'));
const AppointmentDetail = lazyPage(() => import('../pages/appointments/AppointmentDetail'));
const AppointmentCreate = lazyPage(() => import('../pages/appointments/AppointmentCreate'));
const AppointmentCreateWizard = lazyPage(() => import('../pages/appointments/AppointmentCreateWizard'));
const AppointmentEdit = lazyPage(() => import('../pages/appointments/AppointmentEdit'));
const ClassDefinitionList = lazyPage(() => import('../pages/classes/ClassDefinitionList'));
const ClassDefinitionCreate = lazyPage(() => import('../pages/classes/ClassDefinitionCreate'));
const ClassDefinitionDetail = lazyPage(() => import('../pages/classes/ClassDefinitionDetail'));
const ClassDefinitionEdit = lazyPage(() => import('../pages/classes/ClassDefinitionEdit'));
const ClassSessionDetail = lazyPage(() => import('../pages/classes/ClassSessionDetail'));
const WaitlistList = lazyPage(() => import('../pages/waitlist/WaitlistList'));
const SchedulingRequests = lazyPage(() => import('../pages/appointments/SchedulingRequests'));
const SchedulingActivity = lazyPage(() => import('../pages/appointments/SchedulingActivity'));
const AvailabilityManager = lazyPage(() => import('../pages/availability/AvailabilityManager'));
const MessageInbox = lazyPage(() => import('../pages/messages/MessageInbox'));
const MessageThread = lazyPage(() => import('../pages/messages/MessageThread'));
const MessageCompose = lazyPage(() => import('../pages/messages/MessageCompose'));
const VideoRoom = lazyPage(() => import('../components/video/VideoRoom'));
const VideoJoin = lazyPage(() => import('../pages/public/VideoJoin')); // NEW: Public video join page
import { CurrentPatientProvider } from '@/contexts/CurrentPatientContext';
const BillingPage = lazyNamed(() => import('../pages/Billing'), 'BillingPage');
const BillingOverviewTab = lazyNamed(() => import('../components/billing/BillingOverviewTab'), 'BillingOverviewTab');
const UnbilledTab = lazyNamed(() => import('../components/billing/UnbilledTab'), 'UnbilledTab');
const InvoicesTab = lazyNamed(() => import('../components/billing/InvoicesTab'), 'InvoicesTab');
const ClaimsTab = lazyNamed(() => import('../components/billing/ClaimsTab'), 'ClaimsTab');
const RemittancesTab = lazyNamed(() => import('../components/billing/RemittancesTab'), 'RemittancesTab');
const AgingTab = lazyNamed(() => import('../components/billing/AgingTab'), 'AgingTab');
const RTMBillingTab = lazyNamed(() => import('../components/billing/RTMBillingTab'), 'RTMBillingTab');
const PendingAuthorizationTab = lazyNamed(() => import('../components/billing/PendingAuthorizationTab'), 'PendingAuthorizationTab');
const RemovedVisitsTab = lazyNamed(() => import('../components/billing/RemovedVisitsTab'), 'RemovedVisitsTab');
const AddendedVisitsTab = lazyNamed(() => import('../components/billing/AddendedVisitsTab'), 'AddendedVisitsTab');
const SubmittedChargesTab = lazyNamed(() => import('../components/billing/SubmittedChargesTab'), 'SubmittedChargesTab');
const BatchesTab = lazyNamed(() => import('../components/billing/BatchesTab'), 'BatchesTab');
const ClinicalReviewTab = lazyNamed(() => import('../components/billing/ClinicalReviewTab'), 'ClinicalReviewTab');
const HeldClaimsTab = lazyNamed(() => import('../components/billing/HeldClaimsTab'), 'HeldClaimsTab');
const ServicesPage = lazyNamed(() => import('../pages/Services'), 'ServicesPage');
const SubscriptionPlanDetail = lazyNamed(() => import('../pages/SubscriptionPlanDetail'), 'SubscriptionPlanDetail');
const Organization = lazyNamed(() => import('../pages/Organization'), 'Organization');
const Templates = lazyNamed(() => import('../pages/Templates'), 'Templates');
const FeaturesPage = lazyNamed(() => import('../pages/Features'), 'FeaturesPage');
const CollectPaymentPage = lazyNamed(() => import('../pages/billing/CollectPayment'), 'CollectPaymentPage');
const InvoiceDetailPage = lazyNamed(() => import('../pages/billing/InvoiceDetail'), 'InvoiceDetailPage');
const ClaimDetailPage = lazyNamed(() => import('../pages/billing/ClaimDetail'), 'ClaimDetailPage');
const RemittanceDetailPage = lazyNamed(() => import('../pages/billing/RemittanceDetail'), 'RemittanceDetailPage');
const MedicareDataImportPage = lazyNamed(() => import('../pages/billing/MedicareDataImport'), 'MedicareDataImportPage');
const ManualERACreationPage = lazyNamed(() => import('../pages/billing/ManualERACreation'), 'ManualERACreationPage');
const BillingSettingsLayout = lazyNamed(() => import('../pages/billing/settings/BillingSettingsLayout'), 'BillingSettingsLayout');
import { BillingSettingsIndexRedirect } from '../pages/billing/settings/BillingSettingsIndexRedirect';
const PayersTab = lazyNamed(() => import('../pages/billing/settings/PayersTab'), 'PayersTab');
const PayerDetailPage = lazyNamed(() => import('../pages/billing/settings/PayerDetailPage'), 'PayerDetailPage');
const InterventionCatalogTab = lazyNamed(() => import('../pages/billing/settings/InterventionCatalogTab'), 'InterventionCatalogTab');
const BillingRulesTab = lazyNamed(() => import('../pages/billing/settings/BillingRulesTab'), 'BillingRulesTab');
const GeneralBillingSettingsTab = lazyNamed(() => import('../pages/billing/settings/GeneralBillingSettingsTab'), 'GeneralBillingSettingsTab');
const NotificationsPage = lazyPage(() => import('../pages/admin/Notifications'));
const NotificationRuleEditor = lazyPage(() => import('../pages/admin/NotificationRuleEditor'));
const NotificationsLayout = lazyPage(() => import('../pages/admin/NotificationsLayout'));
const NotificationChannelSettings = lazyPage(() => import('../pages/admin/NotificationChannelSettings'));
const NotificationQueue = lazyPage(() => import('../pages/admin/NotificationQueue'));
import NotificationsIndexRedirect from '../pages/admin/NotificationsIndexRedirect';
const BroadcastList = lazyPage(() => import('../pages/admin/broadcasts/BroadcastList'));
const BroadcastComposer = lazyPage(() => import('../pages/admin/broadcasts/BroadcastComposer'));
const BroadcastDetail = lazyPage(() => import('../pages/admin/broadcasts/BroadcastDetail'));
const RehabPrescribedOnboarding = lazyPage(() => import('../pages/onboarding/RehabPrescribedOnboarding'));
const PublicProgramView = lazyPage(() => import('../pages/public/PublicProgramOverview'));
const PublicHepView = lazyPage(() => import('../pages/public/PublicHepView'));
const ScanCapture = lazyPage(() => import('../pages/public/ScanCapture'));
const ScribeHomePage = lazyNamed(() => import('../pages/scribe/ScribeHomePage'), 'ScribeHomePage');
const ScribeAppointmentsPage = lazyNamed(() => import('../pages/scribe/ScribeAppointmentsPage'), 'ScribeAppointmentsPage');
const ScribeEncounterDetails = lazyNamed(() => import('../pages/scribe/ScribeEncounterDetails'), 'ScribeEncounterDetails');


// Lab Orders page
const LabOrdersPage = lazyNamed(() => import('../pages/lab-orders/LabOrdersPage'), 'LabOrdersPage');

// Orders page
const OrdersPage = lazyNamed(() => import('../pages/orders/OrdersPage'), 'OrdersPage');

// Note Templates
const NoteTemplateList = lazyNamed(() => import('../pages/note-templates/NoteTemplateList'), 'NoteTemplateList');
const NoteTemplateBuilder = lazyNamed(() => import('../pages/note-templates/NoteTemplateBuilder'), 'NoteTemplateBuilder');
const TemplateTestRun = lazyNamed(() => import('../pages/note-templates/TemplateTestRun'), 'TemplateTestRun');

// Plan of Care
const PlanOfCareDetailPage = lazyNamed(() => import('../pages/PlanOfCareDetail'), 'PlanOfCareDetailPage');
const PlansOfCareList = lazyNamed(() => import('../pages/PlansOfCareList'), 'PlansOfCareList');

// Home Health (solution-gated; Admins do not bypass the feature guard)
const HhEpisodeList = lazyNamed(() => import('../pages/home-health/EpisodeList'), 'EpisodeList');
const HhEpisodeDetail = lazyNamed(() => import('../pages/home-health/EpisodeDetail'), 'EpisodeDetail');
const HhDashboard = lazyNamed(() => import('../pages/home-health/HomeHealthDashboard'), 'HomeHealthDashboard');
const HhOrderList = lazyNamed(() => import('../pages/home-health/OrderList'), 'OrderList');

// Eligibility Checks
const EligibilityChecksList = lazyNamed(() => import('../pages/EligibilityChecksList'), 'EligibilityChecksList');
const EligibilityCheckDetail = lazyNamed(() => import('../pages/EligibilityCheckDetail'), 'EligibilityCheckDetail');

// Cosign Queue
const CosignQueue = lazyNamed(() => import('../pages/clinical-notes/CosignQueue'), 'CosignQueue');

// Admin — Hub, Roles, Supervision & Referral Sources
const AdminHub = lazyNamed(() => import('../pages/admin/AdminHub'), 'AdminHub');
const VeradigmPMConfig = lazyNamed(() => import('../pages/VeradigmPMConfig'), 'VeradigmPMConfig');
const ModMedConfig = lazyNamed(() => import('../pages/ModMedConfig'), 'ModMedConfig');
const RoleManagementPage = lazyNamed(() => import('../pages/admin/RoleManagement'), 'RoleManagementPage');
const SupervisionManagementPage = lazyNamed(() => import('../pages/admin/SupervisionManagement'), 'SupervisionManagementPage');
const SupervisionStateRulesPage = lazyNamed(() => import('../pages/admin/SupervisionStateRules'), 'SupervisionStateRulesPage');
const CollaborativeAgreementsPage = lazyNamed(() => import('../pages/admin/CollaborativeAgreements'), 'CollaborativeAgreementsPage');
const NPTransitionTrackingPage = lazyNamed(() => import('../pages/admin/NPTransitionTracking'), 'NPTransitionTrackingPage');
const PocCertEndRepairPage = lazyNamed(() => import('../pages/admin/PocCertEndRepair'), 'PocCertEndRepairPage');
const ChartReviewDashboard = lazyNamed(() => import('../pages/clinical-notes/ChartReviewDashboard'), 'ChartReviewDashboard');
const ClinicianReviewPage = lazyNamed(() => import('../pages/ClinicianReviewPage'), 'ClinicianReviewPage');
const ReferralManagementPage = lazyNamed(() => import('../pages/admin/ReferralManagement'), 'ReferralManagementPage');
const ReferralSourceDetailPage = lazyNamed(() => import('../pages/admin/ReferralSourceDetail'), 'ReferralSourceDetailPage');
const PriorAuthorizationsPage = lazyNamed(() => import('../pages/admin/PriorAuthorizations'), 'PriorAuthorizationsPage');
const ReferralsPage = lazyNamed(() => import('../pages/admin/Referrals'), 'ReferralsPage');
const ReportingPage = lazyNamed(() => import('../pages/admin/Reporting'), 'ReportingPage');
const ReportingConfigPage = lazyNamed(() => import('../pages/admin/ReportingConfig'), 'ReportingConfigPage');
const WebPTImportPage = lazyNamed(() => import('../pages/admin/WebPTImport'), 'WebPTImportPage');
const MonthClosePage = lazyNamed(() => import('../pages/admin/MonthClose'), 'MonthClosePage');
const FeedbackPage = lazyNamed(() => import('../pages/admin/Feedback'), 'FeedbackPage');
const FeedbackProviderDetail = lazyNamed(() => import('../pages/admin/feedback/FeedbackProviderDetail'), 'FeedbackProviderDetail');
const FeedbackPatientDetail = lazyNamed(() => import('../pages/admin/feedback/FeedbackPatientDetail'), 'FeedbackPatientDetail');
const TimeTrackingPage = lazyNamed(() => import('../pages/admin/TimeTracking'), 'TimeTrackingPage');
const PtoRequestsPage = lazyNamed(() => import('../pages/admin/PtoRequests'), 'PtoRequestsPage');

// Help Center
const HelpCenter = lazyNamed(() => import('../pages/help/HelpCenter'), 'HelpCenter');

// Fax
const FaxInbox = lazyPage(() => import('../pages/fax/FaxInbox'));
const FaxCompose = lazyPage(() => import('../pages/fax/FaxCompose'));
const FaxDetail = lazyPage(() => import('../pages/fax/FaxDetail'));
const InvitedPatientsPage = lazyNamed(() => import('../pages/InvitedPatients'), 'InvitedPatientsPage');

// Placeholder pages for new features
import {
  EncountersPage,
  ClinicalNotesPage,
  WorkoutBuilderPage,
  TemplatesPage,
  SchedulePage,
  MessagesPage,
  TelehealthPage,
} from '../pages/placeholders';


// Restores the pre-login location after an Auth0 redirect round-trip. The
// return path travels via sessionStorage because Auth0Provider mounts outside
// the router and cannot navigate from its redirect callback.
const PostLoginRedirect = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated) return;
    const returnTo = sessionStorage.getItem('post_login_return_to');
    if (returnTo) {
      sessionStorage.removeItem('post_login_return_to');
      if (returnTo !== window.location.pathname + window.location.search) {
        navigate(returnTo, { replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  return null;
};

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const { user, loading, error, isAuthenticated } = useUser();
  const { isLoading: auth0Loading, logout } = useAuth0();
  const location = useLocation();

  const [isRedirecting, setIsRedirecting] = useState(false);

  // Offline mode: 'blocked' means an offline cold boot found no usable
  // snapshot — render the reconnect wall. Stays 'idle' for non-offline orgs.
  const offlineBootBlocked = useOfflineSessionStore((s) => s.bootState === 'blocked');

  // Bounded fallthrough: authenticated, nothing loading, but no profile yet.
  // Normally UserContext resolves this within a moment; if it never does the
  // user must not sit on a spinner forever, so after PROFILE_WAIT_MS we show a
  // card with Retry / Sign out. The 1 s tick only runs while stalled.
  const PROFILE_WAIT_MS = 10000;
  const stalledSinceRef = useRef(null);
  const [, setStallTick] = useState(0);
  const isAwaitingProfile = isAuthenticated && !user && !loading && !auth0Loading && !isRedirecting;

  useEffect(() => {
    if (!isAwaitingProfile) {
      stalledSinceRef.current = null;
      return;
    }
    if (!stalledSinceRef.current) stalledSinceRef.current = Date.now();
    const id = setInterval(() => setStallTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isAwaitingProfile]);

  const handleSignOut = () => {
    authService.clearToken();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  useEffect(() => {
    // CRITICAL: Wait for both Auth0 and UserContext to finish loading
    if (loading || auth0Loading) {
      return;
    }

    // Reset isRedirecting if we're already on the onboarding page
    if (location.pathname === '/onboarding' && !user?.onboarding?.is_completed) {
      setIsRedirecting(false);
      return;
    }

    if (isRedirecting) return;

    // Only redirect to login if we're certain the user is not authenticated
    if (!isAuthenticated && !loading && !auth0Loading) {
      setIsRedirecting(true);
      navigate("/login", { state: { returnTo: location.pathname }, replace: true });
      return;
    }

    // Handle onboarding redirect
    if (isAuthenticated && user && !user.onboarding?.is_completed && location.pathname !== '/onboarding') {
      setIsRedirecting(true);
      navigate("/onboarding", { state: { returnTo: location.pathname }, replace: true });
    }
  }, [isAuthenticated, user, loading, auth0Loading, location.pathname, navigate, isRedirecting]);

  // Show loading spinner while any loading is happening
  if (loading || auth0Loading || isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Offline mode: cold boot with no usable snapshot — show the reconnect
  // wall instead of a spinner. bootState stays 'idle' for non-offline orgs.
  if (offlineBootBlocked) {
    return <OfflineReconnectWall />;
  }

  // If we've finished loading and user is authenticated with a loaded profile, show the children
  if (isAuthenticated && user) {
    return children;
  }

  // Profile never arrived: stop spinning and give the user a way out
  if (
    isAwaitingProfile &&
    stalledSinceRef.current &&
    Date.now() - stalledSinceRef.current >= PROFILE_WAIT_MS
  ) {
    return <LoadFailureCard detail={error} onSignOut={handleSignOut} />;
  }

  // Otherwise, show loading (will redirect in effect)
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
};

export const isFormsSubdomain = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;

  // Production subdomains
  if (hostname === 'demo-forms.rehabilitationhealth.com' ||
    hostname === 'forms.rehabilitationhealth.com' || hostname === 'demo-form.prismehr.com' || hostname === 'form.prismehr.com') {
    return true;
  }

  // Local development on port 3001
  if (hostname === 'localhost' && port === '3001') {
    return true;
  }

  return false;
};

const isPublicRehabSubdomain = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;

  if (hostname === 'program.rehabprescribed.com' ||
    hostname === 'demo-program.rehabprescribed.com' || hostname === 'program.prismehr.com' || hostname === 'demo-program.prismehr.com') {
    return true;
  }

  if (hostname === 'localhost' && port === '3002') {
    return true;
  }

  return false;
};

// NEW: Check if we're on the video join subdomain
const isVideoJoinSubdomain = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;

  // Production subdomain
  if (hostname === 'join.prismehr.com' || hostname === 'demo-join.prismehr.com') {
    return true;
  }

  // Local development on port 3003
  if (hostname === 'localhost' && port === '3003') {
    return true;
  }

  return false;
};

// Component to determine what to render on the home route
const HomeRouteContent = () => {
  const { solutions, mode, loading, error } = useFeatureFlags();
  const { logout } = useAuth0();

  // FeatureContext gave up waiting for an organization on the profile
  if (error === 'no_organization') {
    return (
      <LoadFailureCard
        detail="Your account is not linked to an organization yet."
        onSignOut={() => {
          authService.clearToken();
          logout({ logoutParams: { returnTo: window.location.origin } });
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Check for prism_scribe mode - redirect to scribe home
  if (mode === 'prism_scribe') {
    return <Navigate to="/scribe" replace />;
  }

  // Check if we're in exercise-only mode
  if (isExerciseOnlyMode(solutions, mode)) {
    return <ExerciseLibraryPage />;
  }

  // Otherwise render the normal home page
  return <HomePage />;
};

const ScribeHomeRouter = () => {
  const { scribeDeploymentType, canToggleScribeMode } = useFeatureFlags();
  // Standalone PrismScribe (only enabled solution): always the encounters list
  if (!canToggleScribeMode) return <ScribeHomePage />;
  return scribeDeploymentType === 'Organization_Wide'
    ? <ScribeAppointmentsPage />
    : <ScribeHomePage />;
};

const AppRouter = () => {
  // Check if we're on the forms subdomain
  if (isFormsSubdomain()) {
    return (
      <Router>
        <RouteErrorBoundary fullPage>
        <Suspense fallback={<FullPageFallback />}>
        <Routes>
          <Route path="/:formId/assistant" element={<AIFormAssistant />} />
          <Route path="/:formId" element={<FormRenderer />} />
          <Route path="/" element={
            <div className="min-h-screen flex items-center justify-center">
              <p>Please provide a form ID in the URL</p>
            </div>
          } />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </Router>
    );
  }

  if (isPublicRehabSubdomain()) {
    return (
      <Router>
        <RouteErrorBoundary fullPage>
        <Suspense fallback={<FullPageFallback />}>
        <Routes>
          <Route path="/programs/:id" element={<PublicProgramView />} />
          <Route path="/hep/:token" element={<PublicHepView />} />
          <Route path="/" element={
            <div className="min-h-screen flex items-center justify-center">
              <p>Please use a program link provided by your provider</p>
            </div>
          } />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </Router>
    );
  }

  // NEW: Video join subdomain routing
  if (isVideoJoinSubdomain()) {
    return (
      <Router>
        <RouteErrorBoundary fullPage>
        <Suspense fallback={<FullPageFallback />}>
        <Routes>
          <Route path="/:token" element={<VideoJoin />} />
          <Route path="/" element={
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h1 className="text-xl font-semibold text-gray-900 mb-2">
                  Video Visit
                </h1>
                <p className="text-gray-600">
                  Please use the video link provided by your healthcare provider to join your appointment.
                </p>
              </div>
            </div>
          } />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </Router>
    );
  }

  return (
    <Router>
      <PostLoginRedirect />
      <RouteErrorBoundary fullPage>
      <Suspense fallback={<FullPageFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/scan" element={<ScanCapture />} />
        <Route path="/onboarding/rehab" element={<RehabPrescribedOnboarding />} />

        {/* Veradigm Callback - moved inside protected routes */}
        <Route
          path="/veradigm/callback"
          element={
            <ProtectedRoute>
              <VeradigmCallbackHandler />
            </ProtectedRoute>
          }
        />

        {/* Google Calendar Callback */}
        <Route
          path="/auth/google-calendar/callback"
          element={
            <ProtectedRoute>
              <GoogleCalendarCallbackHandler />
            </ProtectedRoute>
          }
        />

        {/* Onboarding Route */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingFlow />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <CurrentPatientProvider>
                <DashboardLayout>
                  <RouteErrorBoundary>
                  <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    {/* Home - conditionally renders HomePage or ExerciseLibraryPage */}
                    <Route index element={<HomeRouteContent />} />

                    {/* Patients - always available */}
                    <Route path="/patients" element={<PatientsPage />} />
                    <Route path="/patients/new" element={<PatientEditPage />} />
                    <Route path="/patients/:id" element={<PatientDetailsPage />} />
                    <Route path="/patients/:id/edit" element={<PatientEditPage />} />
                    <Route path="/patients/:patientId/complete-form/:formId" element={
                      <FeatureGuard requiredFeature="form_builder">
                        <CompleteFormPage />
                      </FeatureGuard>
                    } />
                    <Route path="/invited-patients" element={<InvitedPatientsPage />} />

                    {/* Profile - always available */}
                    <Route path="/profile" element={<ProfilePage />} />

                    {/* Help Center - always available */}
                    <Route path="/help" element={<HelpCenter />} />
                    <Route path="/help/:category" element={<HelpCenter />} />
                    <Route path="/help/:category/:slug" element={<HelpCenter />} />

                    {/* eRx Mobile Routes */}
                    <Route path="/medications" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <MedicationsPage />
                      </FeatureGuard>
                    } />
                    <Route path="/prescriptions" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <PrescriptionHistoryPage />
                      </FeatureGuard>
                    } />
                    <Route path="/prescriptions/create" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <PrescriptionCreatePage />
                      </FeatureGuard>
                    } />
                    <Route path="/prescriptions/review" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <PrescriptionReviewPage />
                      </FeatureGuard>
                    } />
                    <Route path="/prescriptions/:id" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <PrescriptionDetailPage />
                      </FeatureGuard>
                    } />
                    <Route path="/renewal-requests" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <RenewalRequestsPage />
                      </FeatureGuard>
                    } />
                    <Route path="/renewal-requests/:id" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <RenewalRequestDetailPage />
                      </FeatureGuard>
                    } />
                    <Route path="/change-requests" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <ChangeRequestsPage />
                      </FeatureGuard>
                    } />
                    <Route path="/change-requests/:id" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <ChangeRequestDetail />
                      </FeatureGuard>
                    } />

                    {/* DEA Compliance Routes */}
                    <Route path="/audit-logs" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <PermissionGuard requiredPermission="admin_view_audit_log">
                          <AuditLogDashboard />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />
                    <Route path="/reports/controlled-substances" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <MonthlyControlledSubstanceReport />
                      </FeatureGuard>
                    } />
                    <Route path="/reports/controlled-substances/:year/:month" element={
                      <FeatureGuard requiredFeature="erx_mobile">
                        <MonthlyControlledSubstanceReport />
                      </FeatureGuard>
                    } />

                    {/* Core EHR Routes */}
                    <Route path="/encounters" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <EncounterList />
                      </FeatureGuard>
                    } />
                    <Route path="/encounters/new" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <EncounterCreate />
                      </FeatureGuard>
                    } />
                    <Route path="/encounters/:id" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <EncounterDetail />
                      </FeatureGuard>
                    } />
                    <Route path="/encounters/:id/edit" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <EncounterEdit />
                      </FeatureGuard>
                    } />

                    <Route path="/clinical-notes" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <ClinicalNoteList />
                      </FeatureGuard>
                    } />
                    <Route path="/clinical-notes/new" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <PermissionGuard requiredPermission="clinical_create_notes">
                          <ClinicalNoteCreate />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />
                    <Route path="/visits/:encounterId" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <VisitSummary />
                      </FeatureGuard>
                    } />
                    <Route path="/clinical-notes/:id" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <ClinicalNoteDetail />
                      </FeatureGuard>
                    } />
                    <Route path="/clinical-notes/:id/edit" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <ClinicalNoteEdit />
                      </FeatureGuard>
                    } />

                    {/* Note Template Routes */}
                    <Route path="/note-templates" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <PermissionGuard requiredPermission="clinical_manage_templates">
                          <NoteTemplateList />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />
                    <Route path="/note-templates/test-run" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <PermissionGuard requiredPermission="clinical_manage_templates">
                          <TemplateTestRun />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />
                    <Route path="/note-templates/new" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <PermissionGuard requiredPermission="clinical_manage_templates">
                          <NoteTemplateBuilder />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />
                    <Route path="/note-templates/:id/edit" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <PermissionGuard requiredPermission="clinical_manage_templates">
                          <NoteTemplateBuilder />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />

                    {/* Plan of Care Routes */}
                    <Route path="/plans-of-care" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <PlansOfCareList />
                      </FeatureGuard>
                    } />
                    <Route path="/plan-of-care/:id" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <PlanOfCareDetailPage />
                      </FeatureGuard>
                    } />

                    <Route path="/lab-orders" element={
                      <FeatureGuard requiredFeature="core_ehr">
                        <LabOrdersPage />
                      </FeatureGuard>
                    } />

                    <Route path="/orders" element={
                      <FeatureGuard requiredFeature="orders">
                        <OrdersPage />
                      </FeatureGuard>
                    } />

                    {/* Home Health Routes */}
                    <Route path="/home-health" element={
                      <FeatureGuard requiredFeature="home_health" adminOverride={false}>
                        <PermissionGuard requiredPermission="hh_view">
                          <HhDashboard />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />
                    <Route path="/home-health/orders" element={
                      <FeatureGuard requiredFeature="home_health" adminOverride={false}>
                        <PermissionGuard requiredPermission="hh_view">
                          <HhOrderList />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />
                    <Route path="/home-health/episodes" element={
                      <FeatureGuard requiredFeature="home_health" adminOverride={false}>
                        <PermissionGuard requiredPermission="hh_view">
                          <HhEpisodeList />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />
                    <Route path="/home-health/episodes/:id" element={
                      <FeatureGuard requiredFeature="home_health" adminOverride={false}>
                        <PermissionGuard requiredPermission="hh_view">
                          <HhEpisodeDetail />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />

                    {/* Scribe Routes */}
                    {/* PrismScribe Routes */}
                    <Route path="/scribe" element={
                      <FeatureGuard requiredFeature="prism_scribe">
                        <ScribeHomeRouter />
                      </FeatureGuard>
                    } />
                    <Route path="/scribe/encounter/:id" element={
                      <FeatureGuard requiredFeature="prism_scribe">
                        <ScribeEncounterDetails />
                      </FeatureGuard>
                    } />

                    {/* Exercise Routes - Keep /exercises route for backwards compatibility */}
                    <Route path="/exercises" element={
                      <FeatureGuard requiredFeatures={['rehab_prescribed', 'ready2exercise']} requireAny>
                        <ExerciseLibraryPage />
                      </FeatureGuard>
                    } />
                    <Route path="/templates" element={
                      <FeatureGuard requiredFeatures={['rehab_prescribed', 'ready2exercise']} requireAny>
                        <Templates />
                      </FeatureGuard>
                    } />

                    {/* Rehab Prescribed Routes */}
                    <Route path="/cpg-library" element={
                      <FeatureGuard requiredFeature="rehab_prescribed">
                        <CPGLibraryPage />
                      </FeatureGuard>
                    } />
                    <Route path="/rehab-programs" element={
                      <FeatureGuard requiredFeature="rehab_prescribed">
                        <RehabProgramsPage />
                      </FeatureGuard>
                    } />
                    <Route path="/rehab-programs/:id" element={
                      <FeatureGuard requiredFeature="rehab_prescribed">
                        <RehabProgramDetailPage />
                      </FeatureGuard>
                    } />
                    <Route path="/rehab-programs/:id/edit" element={
                      <FeatureGuard requiredFeature="rehab_prescribed">
                        <RehabProgramEditPage />
                      </FeatureGuard>
                    } />
                    <Route path="/routines/:id" element={
                      <FeatureGuard requiredFeatures={['rehab_prescribed', 'ready2exercise']} requireAny>
                        <RoutineDetailPage />
                      </FeatureGuard>
                    } />

                    {/* Ready2Exercise Routes */}
                    <Route path="/workout-builder" element={
                      <FeatureGuard requiredFeature="ready2exercise">
                        <WorkoutBuilderPage />
                      </FeatureGuard>
                    } />
                    <Route path="/workout-plans" element={
                      <FeatureGuard requiredFeature="ready2exercise">
                        <WorkoutPlans />
                      </FeatureGuard>
                    } />
                    <Route path="/workout-plans/:id" element={
                      <FeatureGuard requiredFeature="ready2exercise">
                        <WorkoutPlanDetail />
                      </FeatureGuard>
                    } />

                    {/* Form Builder Routes */}
                    <Route path="/forms" element={
                      <FeatureGuard requiredFeature="form_builder">
                        <Forms />
                      </FeatureGuard>
                    } />
                    <Route path="/responses" element={
                      <FeatureGuard requiredFeature="form_builder">
                        <Responses />
                      </FeatureGuard>
                    } />
                    <Route path="/responses/:id" element={
                      <FeatureGuard requiredFeature="form_builder">
                        <ResponseDetails />
                      </FeatureGuard>
                    } />
                    <Route path="/responses/:id/edit" element={
                      <FeatureGuard requiredFeature="form_builder">
                        <ResponseEditor />
                      </FeatureGuard>
                    } />
                    <Route path="/form-reports" element={
                      <FeatureGuard requiredFeature="form_builder">
                        <Reports />
                      </FeatureGuard>
                    } />
                    <Route path="/reports/:id" element={
                      <FeatureGuard requiredFeature="form_builder">
                        <ReportDetails />
                      </FeatureGuard>
                    } />
                    <Route path="/form-send-history" element={
                      <FeatureGuard requiredFeature="form_builder">
                        <FormSendHistory />
                      </FeatureGuard>
                    } />

                    {/* Scheduling Routes */}
                    <Route path="/appointments" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <AppointmentList />
                      </FeatureGuard>
                    } />
                    <Route path="/appointments/calendar" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <AppointmentCalendar />
                      </FeatureGuard>
                    } />
                    <Route path="/appointments/new" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <AppointmentCreateWizard />
                      </FeatureGuard>
                    } />
                    <Route path="/appointments/quick-create" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <AppointmentCreate />
                      </FeatureGuard>
                    } />
                    <Route path="/appointments/requests" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <SchedulingRequests />
                      </FeatureGuard>
                    } />
                    <Route path="/appointments/activity" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <PermissionGuard requiredPermission="scheduling_view_activity">
                          <SchedulingActivity />
                        </PermissionGuard>
                      </FeatureGuard>
                    } />
                    <Route path="/appointments/:id" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <AppointmentDetail />
                      </FeatureGuard>
                    } />
                    <Route path="/appointments/:id/edit" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <AppointmentEdit />
                      </FeatureGuard>
                    } />
                    <Route path="/appointments/:id/video" element={
                      <FeatureGuard requiredFeature="telehealth">
                        <VideoRoom />
                      </FeatureGuard>
                    } />
                    <Route path="/availability" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <AvailabilityManager />
                      </FeatureGuard>
                    } />

                    {/* Class Routes */}
                    <Route path="/classes" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <ClassDefinitionList />
                      </FeatureGuard>
                    } />
                    <Route path="/classes/new" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <ClassDefinitionCreate />
                      </FeatureGuard>
                    } />
                    <Route path="/classes/:id" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <ClassDefinitionDetail />
                      </FeatureGuard>
                    } />
                    <Route path="/classes/:id/edit" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <ClassDefinitionEdit />
                      </FeatureGuard>
                    } />
                    <Route path="/classes/sessions/:sessionId" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <ClassSessionDetail />
                      </FeatureGuard>
                    } />

                    {/* Waitlist Route */}
                    <Route path="/waitlist" element={
                      <FeatureGuard requiredFeature="scheduling">
                        <WaitlistList />
                      </FeatureGuard>
                    } />

                    {/* Legacy route redirect for backward compatibility */}
                    <Route path="/schedule" element={
                      <Navigate to="/appointments" replace />
                    } />

                    {/* Messaging Routes */}
                    <Route path="/messages" element={
                      <FeatureGuard requiredFeature="messaging">
                        <MessageInbox />
                      </FeatureGuard>
                    } />
                    <Route path="/messages/new" element={
                      <FeatureGuard requiredFeature="messaging">
                        <MessageCompose />
                      </FeatureGuard>
                    } />
                    <Route path="/messages/:threadId" element={
                      <FeatureGuard requiredFeature="messaging">
                        <MessageThread />
                      </FeatureGuard>
                    } />

                    {/* Fax Routes */}
                    <Route path="/fax" element={
                      <FeatureGuard requiredFeature="fax">
                        <FaxInbox />
                      </FeatureGuard>
                    } />
                    <Route path="/fax/new" element={
                      <FeatureGuard requiredFeature="fax">
                        <FaxCompose />
                      </FeatureGuard>
                    } />
                    <Route path="/fax/:id" element={
                      <FeatureGuard requiredFeature="fax">
                        <FaxDetail />
                      </FeatureGuard>
                    } />

                    {/* Future Features */}
                    <Route path="/telehealth" element={
                      <FeatureGuard requiredFeature="telehealth">
                        <TelehealthPage />
                      </FeatureGuard>
                    } />

                    {/* Clinicians — read-only directory */}
                    <Route path="/clinicians" element={
                      <PermissionGuard requiredPermission="view_clinicians">
                        <CliniciansPage />
                      </PermissionGuard>
                    } />
                    <Route path="/clinicians/:id" element={
                      <PermissionGuard requiredPermission="view_clinicians">
                        <UserDetailPage readOnly />
                      </PermissionGuard>
                    } />

                    {/* Admin Routes */}
                    <Route path="/admin" element={<AdminHub />} />
                    <Route path="/admin/veradigm-pm" element={<VeradigmPMConfig />} />
                    <Route path="/admin/modmed" element={<ModMedConfig />} />
                    {/* One-off repair tool; access gated inside the page to a single user id */}
                    <Route path="/admin/poc-cert-end-repair" element={<PocCertEndRepairPage />} />
                    <Route path="/users" element={
                      <PermissionGuard requiredPermission="admin_manage_users">
                        <UsersPage />
                      </PermissionGuard>
                    } />
                    <Route path="/users/new" element={
                      <PermissionGuard requiredPermission="admin_manage_users">
                        <UserCreatePage />
                      </PermissionGuard>
                    } />
                    <Route path="/users/:id" element={
                      <PermissionGuard requiredPermission="admin_manage_users">
                        <UserDetailPage />
                      </PermissionGuard>
                    } />
                    <Route path="/users/:id/edit" element={
                      <PermissionGuard requiredPermission="admin_manage_users">
                        <UserEditPage />
                      </PermissionGuard>
                    } />
                    <Route path="/roles" element={
                      <PermissionGuard requiredPermission="admin_manage_roles">
                        <RoleManagementPage />
                      </PermissionGuard>
                    } />
                    <Route path="/supervision" element={
                      <PermissionGuard requiredPermission="admin_manage_users">
                        <SupervisionManagementPage />
                      </PermissionGuard>
                    } />
                    <Route path="/supervision-rules" element={
                      <PermissionGuard requiredPermission="admin_manage_supervision_rules">
                        <SupervisionStateRulesPage />
                      </PermissionGuard>
                    } />
                    <Route path="/cosign-queue" element={
                      <PermissionGuard requiredPermissions={['clinical_cosign_notes', 'clinical_sign_notes']} requireAny>
                        <CosignQueue />
                      </PermissionGuard>
                    } />
                    <Route path="/collaborative-agreements" element={
                      <PermissionGuard requiredPermission="admin_manage_collaborative_agreements">
                        <CollaborativeAgreementsPage />
                      </PermissionGuard>
                    } />
                    <Route path="/np-transition" element={
                      <PermissionGuard requiredPermission="admin_manage_supervision_rules">
                        <NPTransitionTrackingPage />
                      </PermissionGuard>
                    } />
                    <Route path="/chart-review" element={
                      <PermissionGuard requiredPermission="clinical_review_charts">
                        <ChartReviewDashboard />
                      </PermissionGuard>
                    } />
                    {/* Access gated inside the page on the role's clinical_review home badge */}
                    <Route path="/clinical-review" element={<ClinicianReviewPage />} />
                    <Route path="/services" element={
                      <PermissionGuard requiredPermission="admin_manage_org_settings">
                        <ServicesPage />
                      </PermissionGuard>
                    } />
                    <Route path="/services/subscriptions/:id" element={
                      <PermissionGuard requiredPermission="admin_manage_org_settings">
                        <SubscriptionPlanDetail />
                      </PermissionGuard>
                    } />
                    <Route path="/interventions" element={<Navigate to="/billing/settings/catalog" replace />} />
                    <Route path="/organization" element={
                      <PermissionGuard requiredPermission="admin_manage_org_settings">
                        <Organization />
                      </PermissionGuard>
                    } />
                    <Route path="/notifications" element={
                      <PermissionGuard requiredPermissions={['admin_manage_org_settings', 'notifications_view']} requireAny>
                        <NotificationsLayout />
                      </PermissionGuard>
                    }>
                      <Route index element={<NotificationsIndexRedirect />} />
                      <Route path="queue" element={<NotificationQueue />} />
                      <Route path="rules" element={
                        <PermissionGuard requiredPermission="admin_manage_org_settings">
                          <NotificationsPage />
                        </PermissionGuard>
                      } />
                      <Route path="rules/new" element={
                        <PermissionGuard requiredPermission="admin_manage_org_settings">
                          <NotificationRuleEditor />
                        </PermissionGuard>
                      } />
                      <Route path="rules/:ruleId" element={
                        <PermissionGuard requiredPermission="admin_manage_org_settings">
                          <NotificationRuleEditor />
                        </PermissionGuard>
                      } />
                      <Route path="broadcasts" element={
                        <PermissionGuard requiredPermission="admin_manage_org_settings">
                          <BroadcastList />
                        </PermissionGuard>
                      } />
                      <Route path="broadcasts/new" element={
                        <PermissionGuard requiredPermission="admin_manage_org_settings">
                          <BroadcastComposer />
                        </PermissionGuard>
                      } />
                      <Route path="broadcasts/:broadcastId/edit" element={
                        <PermissionGuard requiredPermission="admin_manage_org_settings">
                          <BroadcastComposer />
                        </PermissionGuard>
                      } />
                      <Route path="broadcasts/:broadcastId" element={
                        <PermissionGuard requiredPermission="admin_manage_org_settings">
                          <BroadcastDetail />
                        </PermissionGuard>
                      } />
                      <Route path="settings" element={
                        <PermissionGuard requiredPermission="admin_manage_org_settings">
                          <NotificationChannelSettings />
                        </PermissionGuard>
                      } />
                    </Route>
                    <Route path="/features" element={
                      <PermissionGuard requiredPermission="admin_manage_org_settings">
                        <FeaturesPage />
                      </PermissionGuard>
                    } />
                    <Route path="/referral-sources" element={
                      <PermissionGuard requiredPermission="referrals_manage">
                        <ReferralManagementPage />
                      </PermissionGuard>
                    } />
                    <Route path="/referral-sources/provider/:id" element={
                      <PermissionGuard requiredPermission="referrals_manage">
                        <ReferralSourceDetailPage kind="provider" />
                      </PermissionGuard>
                    } />
                    <Route path="/referral-sources/organization/:id" element={
                      <PermissionGuard requiredPermission="referrals_manage">
                        <ReferralSourceDetailPage kind="organization" />
                      </PermissionGuard>
                    } />
                    <Route path="/prior-authorizations" element={
                      <PermissionGuard requiredPermissions={['admin_manage_org_settings', 'billing_prior_auth_view']} requireAny>
                        <PriorAuthorizationsPage />
                      </PermissionGuard>
                    } />
                    <Route path="/referrals" element={
                      <PermissionGuard requiredPermission="referrals_view">
                        <ReferralsPage />
                      </PermissionGuard>
                    } />
                    <Route path="/reporting" element={
                      <PermissionGuard requiredPermission="reporting_view">
                        <ReportingPage />
                      </PermissionGuard>
                    } />
                    <Route path="/reporting-config" element={
                      <PermissionGuard requiredPermission="admin_manage_org_settings">
                        <ReportingConfigPage />
                      </PermissionGuard>
                    } />
                    <Route path="/webpt-import" element={
                      <PermissionGuard requiredPermission="admin_manage_org_settings">
                        <WebPTImportPage />
                      </PermissionGuard>
                    } />
                    <Route path="/month-close" element={
                      <PermissionGuard requiredPermission="reporting_view">
                        <MonthClosePage />
                      </PermissionGuard>
                    } />
                    <Route path="/feedback" element={
                      <PermissionGuard requiredPermissions={['feedback_view_all', 'feedback_view_own']} requireAny>
                        <FeedbackPage />
                      </PermissionGuard>
                    } />
                    <Route path="/feedback/:providerId" element={
                      <PermissionGuard requiredPermissions={['feedback_view_all', 'feedback_view_own']} requireAny>
                        <FeedbackProviderDetail />
                      </PermissionGuard>
                    } />
                    <Route path="/feedback/patient/:patientId" element={
                      <PermissionGuard requiredPermissions={['feedback_view_all', 'feedback_view_own']} requireAny>
                        <FeedbackPatientDetail />
                      </PermissionGuard>
                    } />
                    <Route path="/time-tracking" element={
                      <PermissionGuard requiredPermission="time_tracking_view">
                        <TimeTrackingPage />
                      </PermissionGuard>
                    } />
                    <Route path="/pto-requests" element={
                      <PermissionGuard requiredPermission="pto_manage">
                        <PtoRequestsPage />
                      </PermissionGuard>
                    } />

                    {/* Billing Routes — feature + permission gated */}
                    <Route path="/billing" element={
                      <FeatureGuard requiredFeature="billing">
                        <PermissionGuard requiredPermissions={['billing_view_own', 'billing_view_all', 'billing_manage']} requireAny>
                          <BillingPage />
                        </PermissionGuard>
                      </FeatureGuard>
                    }>
                      <Route index element={<Navigate to="unbilled" replace />} />
                      <Route path="overview" element={<BillingOverviewTab />} />
                      <Route path="unbilled" element={<UnbilledTab />} />
                      <Route path="invoices" element={<InvoicesTab />} />
                      <Route path="claims" element={<ClaimsTab />} />
                      <Route path="remittances" element={<RemittancesTab />} />
                      <Route path="aging" element={<AgingTab />} />
                      <Route path="clinical-review" element={<ClinicalReviewTab />} />
                      <Route path="held-claims" element={<HeldClaimsTab />} />
                      <Route path="rtm" element={<RTMBillingTab />} />
                      <Route path="pending-auth" element={<PendingAuthorizationTab />} />
                      <Route path="removed" element={<RemovedVisitsTab />} />
                      <Route path="submitted-charges" element={<SubmittedChargesTab />} />
                      <Route path="addended" element={<AddendedVisitsTab />} />
                      <Route path="batches" element={<BatchesTab />} />
                    </Route>
                    <Route path="/billing/collect-payment" element={
                      <PermissionGuard requiredPermission="billing_manage">
                        <CollectPaymentPage />
                      </PermissionGuard>
                    } />
                    <Route path="/billing/invoices/:id" element={
                      <PermissionGuard requiredPermissions={['billing_view_own', 'billing_view_all', 'billing_manage']} requireAny>
                        <InvoiceDetailPage />
                      </PermissionGuard>
                    } />
                    <Route path="/billing/claims/:id" element={
                      <PermissionGuard requiredPermissions={['billing_view_own', 'billing_view_all', 'billing_manage']} requireAny>
                        <ClaimDetailPage />
                      </PermissionGuard>
                    } />
                    <Route path="/billing/remittances/create" element={
                      <PermissionGuard requiredPermission="billing_manage">
                        <ManualERACreationPage />
                      </PermissionGuard>
                    } />
                    <Route path="/billing/remittances/:id" element={
                      <PermissionGuard requiredPermissions={['billing_view_own', 'billing_view_all', 'billing_manage']} requireAny>
                        <RemittanceDetailPage />
                      </PermissionGuard>
                    } />
                    <Route path="/billing/fee-schedule" element={<Navigate to="/billing/settings" replace />} />
                    <Route path="/billing/settings" element={
                      <PermissionGuard requiredPermissions={['billing_view_all', 'billing_manage', 'admin_manage_org_settings']} requireAny>
                        <BillingSettingsLayout />
                      </PermissionGuard>
                    }>
                      <Route index element={<BillingSettingsIndexRedirect />} />
                      <Route path="payers" element={<PayersTab />} />
                      <Route path="payers/:payerId" element={<PayerDetailPage />} />
                      <Route path="catalog" element={<InterventionCatalogTab />} />
                      <Route path="rules" element={<BillingRulesTab />} />
                      <Route path="general" element={<GeneralBillingSettingsTab />} />
                    </Route>
                    <Route path="/billing/medicare-data" element={
                      <PermissionGuard requiredPermission="billing_manage">
                        <MedicareDataImportPage />
                      </PermissionGuard>
                    } />

                    {/* Eligibility Checks */}
                    <Route path="/eligibility-checks" element={
                      <FeatureGuard requiredFeature="billing">
                        <EligibilityChecksList />
                      </FeatureGuard>
                    } />
                    <Route path="/eligibility-checks/:id" element={
                      <FeatureGuard requiredFeature="billing">
                        <EligibilityCheckDetail />
                      </FeatureGuard>
                    } />
                  </Routes>
                  </Suspense>
                  </RouteErrorBoundary>
                </DashboardLayout>
              </CurrentPatientProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
      </Suspense>
      </RouteErrorBoundary>
    </Router>
  );
};

export default AppRouter;